import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useDispatch, useSelector } from "react-redux";
import {
  approveRfidEndorsement,
  getRfidEndorsements,
} from "../../action/RfidEndorsementActions";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const mapLink = (latitude, longitude) =>
  latitude && longitude
    ? `https://www.google.com/maps?q=${latitude},${longitude}`
    : null;

export default function RfidEndorsementApproval() {
  const dispatch = useDispatch();
  const { cdl, subcontract, loading, error } = useSelector(
    (state) => state.rfidEndorsements
  );
  const [activeTab, setActiveTab] = useState(0);
  const [approvingKey, setApprovingKey] = useState(null);
  const [rejectingRow, setRejectingRow] = useState(null);
  const [notice, setNotice] = useState({ open: false, message: "", severity: "success" });
  const isCdl = activeTab === 0;
  const rows = isCdl ? cdl : subcontract;

  useEffect(() => {
    dispatch(getRfidEndorsements()).catch(() => {});
  }, [dispatch]);

  const updateEndorsement = async (
    row,
    status = "I",
    kind = isCdl ? "cdl" : "subcontract"
  ) => {
    const key = kind === "cdl" ? row.HBL_ID : `${row.TFT_UID}-${row.TFT_DATETIME}`;
    setApprovingKey(key);
    try {
      await dispatch(
        approveRfidEndorsement(kind, row, status)
      );
      setNotice({
        open: true,
        message: status === "R" ? "Endorsement rejected." : "Endorsement approved.",
        severity: status === "R" ? "info" : "success",
      });
    } catch (approvalError) {
      setNotice({
        open: true,
        message:
          approvalError.response?.data?.message ||
          `Could not ${status === "R" ? "reject" : "approve"} endorsement.`,
        severity: "error",
      });
    } finally {
      setApprovingKey(null);
    }
  };

  const renderLocation = (location, latitude, longitude) => {
    const link = mapLink(latitude, longitude);
    return (
      <>
        <div>{location || "-"}</div>
        {link && (
          <a href={link} target="_blank" rel="noreferrer">
            View map
          </a>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 1.5, sm: 2.5 },
        background: "linear-gradient(135deg, #F4F8FD 0%, #FFFFFF 52%, #E6EEF9 100%)",
      }}
    >
      <Box sx={{ maxWidth: 1250, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          mb: 2.5,
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          color: "#fff",
          background: "linear-gradient(135deg, #004AAD 0%, #1A5FC4 100%)",
          boxShadow: "0 8px 24px rgba(0, 74, 173, 0.2)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <AssignmentTurnedInIcon sx={{ fontSize: { xs: 30, sm: 38 } }} />
          <Box>
          <Typography variant="h5" fontWeight={700}>
            RFID Endorsement Approval
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,.82)" }}>
            Pending endorsements: {cdl.length + subcontract.length}
          </Typography>
          </Box>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box
        sx={{
          overflow: "hidden",
          borderRadius: { xs: 0, md: 2 },
          border: { xs: "none", md: "1px solid #C5D8F0" },
          boxShadow: { xs: "none", md: "0 10px 32px rgba(0, 74, 173, 0.09)" },
          bgcolor: { xs: "transparent", md: "#fff" },
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          aria-label="Endorsement type"
          variant="fullWidth"
          sx={{
            bgcolor: "#F4F8FD",
            borderBottom: "1px solid #C5D8F0",
            "& .MuiTab-root": {
              minHeight: 54,
              textTransform: "none",
              fontWeight: 700,
              color: "#52657D",
            },
            "& .Mui-selected": { color: "#004AAD !important" },
            "& .MuiTabs-indicator": { height: 3, bgcolor: "#004AAD" },
          }}
        >
          <Tab label={`CDL (${cdl.length})`} />
          <Tab label={`Subcontract (${subcontract.length})`} />
        </Tabs>
        <TableContainer
          sx={{
            maxHeight: { xs: "none", md: "calc(100vh - 280px)" },
            overflowX: "auto",
          }}
        >
          <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 1.25, p: { xs: 1, sm: 1.5 } }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                <CircularProgress size={28} />
              </Box>
            ) : rows.length === 0 ? (
              <Typography align="center" color="text.secondary" sx={{ py: 5 }}>
                No pending endorsements.
              </Typography>
            ) : (
              rows.map((row) => {
                const key = isCdl
                  ? row.HBL_ID
                  : `${row.TFT_UID}-${row.TFT_DATETIME}`;
                const location = isCdl ? row.HBL_LOCATION : row.TFT_LOCATION;
                const latitude = isCdl ? row.HBL_LATITUDE : row.TFT_LATITUDE;
                const longitude = isCdl ? row.HBL_LONGITUDE : row.TFT_LONGITUDE;
                return (
                  <Paper
                    key={key}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      borderColor: "#C5D8F0",
                      boxShadow: "0 3px 12px rgba(0, 74, 173, 0.07)",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ color: "#004AAD", fontWeight: 700 }}>
                        {row.NAME || "Unknown employee"}
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          alignSelf: "flex-start",
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          bgcolor: "#FFF3E0",
                          color: "#A34A00",
                          fontSize: 11,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Pending
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25 }}>
                      {formatDate(isCdl ? row.HBL_BARCODE_DATE : row.TFT_DATETIME)}
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1.25 }}>
                        <Box>
                        <Typography variant="caption" color="text.secondary">
                          {isCdl ? "Card No" : "NIC"}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {isCdl ? row.HBL_BARCODE_NO : row.TFT_NIC || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {isCdl ? "Clock No" : "User ID"}
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {isCdl ? row.HBL_CLOCK_NO : row.TFT_UID}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ gridColumn: "1 / -1" }}>
                        <Typography variant="caption" color="text.secondary">
                          Location
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {location || "-"}
                          {/* {latitude && longitude && (
                            <> · <a href={mapLink(latitude, longitude)} target="_blank" rel="noreferrer">Map</a></>
                          )} */}
                        </Typography>
                      </Box>
                      <Box sx={{ gridColumn: "1 / -1" }}>
                        <Typography variant="caption" color="text.secondary">
                          {isCdl ? "Reason" : "Remark"}
                        </Typography>
                        <Typography variant="body2">
                          {(isCdl ? row.HBL_REASON : row.TFT_REMARK) || "-"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, "& > button": { flex: 1 } }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleOutlineIcon />}
                        onClick={() => updateEndorsement(row)}
                        disabled={Boolean(approvingKey)}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<CancelOutlinedIcon />}
                        onClick={() => setRejectingRow({ row, kind: isCdl ? "cdl" : "subcontract" })}
                        disabled={Boolean(approvingKey)}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        Reject
                      </Button>
                    </Box>
                  </Paper>
                );
              })
            )}
          </Box>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Table stickyHeader size="small" aria-label="Pending endorsements" sx={{ minWidth: 900 }}>
            <TableHead
              sx={{
                "& .MuiTableCell-head": {
                  bgcolor: "#004AAD",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: ".04em",
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableRow>
                <TableCell>Date and time</TableCell>
                <TableCell>{isCdl ? "Clock No" : "User ID"}</TableCell>
                <TableCell>Name</TableCell>
                {isCdl ? <TableCell>Card No</TableCell> : <TableCell>NIC</TableCell>}
                <TableCell>Location</TableCell>
                <TableCell>{isCdl ? "Reason" : "Remark"}</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    No pending endorsements.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const key = isCdl
                    ? row.HBL_ID
                    : `${row.TFT_UID}-${row.TFT_DATETIME}`;
                  return (
                    <TableRow
                      hover
                      key={key}
                      sx={{
                        "&:nth-of-type(even)": { bgcolor: "#F4F8FD" },
                        "&:hover": { bgcolor: "#E6EEF9 !important" },
                      }}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600, color: "#334155" }}>
                        {formatDate(isCdl ? row.HBL_BARCODE_DATE : row.TFT_DATETIME)}
                      </TableCell>
                      <TableCell sx={{ color: "#004AAD", fontWeight: 700 }}>
                        {isCdl ? row.HBL_CLOCK_NO : row.TFT_UID}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.NAME || "-"}</TableCell>
                      <TableCell>{isCdl ? row.HBL_BARCODE_NO : row.TFT_NIC}</TableCell>
                      <TableCell>
                        {renderLocation(
                          isCdl ? row.HBL_LOCATION : row.TFT_LOCATION,
                          isCdl ? row.HBL_LATITUDE : row.TFT_LATITUDE,
                          isCdl ? row.HBL_LONGITUDE : row.TFT_LONGITUDE
                        )}
                      </TableCell>
                      <TableCell>{(isCdl ? row.HBL_REASON : row.TFT_REMARK) || "-"}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleOutlineIcon />}
                            onClick={() => updateEndorsement(row)}
                            disabled={Boolean(approvingKey)}
                            sx={{ borderRadius: 1, textTransform: "none", fontWeight: 700 }}
                          >
                            {approvingKey === key ? "Updating" : "Approve"}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<CancelOutlinedIcon />}
                            onClick={() => setRejectingRow({ row, kind: isCdl ? "cdl" : "subcontract" })}
                            disabled={Boolean(approvingKey)}
                            sx={{ borderRadius: 1, textTransform: "none", fontWeight: 700 }}
                          >
                            Reject
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          </Box>
        </TableContainer>
      </Box>
      </Box>

      <Dialog open={Boolean(rejectingRow)} onClose={() => setRejectingRow(null)}>
        <DialogTitle>
          Reject {rejectingRow?.kind === "cdl" ? "CDL" : "Subcontract"} endorsement?
        </DialogTitle>
        <DialogContent>
          This will mark the endorsement as rejected and remove it from the pending list.
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectingRow(null)} sx={{ textTransform: "none" }}>
            Keep pending
          </Button>
          <Button
            color="error"
            variant="contained"
            startIcon={<CancelOutlinedIcon />}
            disabled={Boolean(approvingKey)}
            onClick={async () => {
              const rejection = rejectingRow;
              setRejectingRow(null);
              await updateEndorsement(rejection.row, "R", rejection.kind);
            }}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Reject endorsement
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notice.open}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setNotice((previous) => ({ ...previous, open: false }))}
        sx={{ top: { xs: 12, sm: 24 } }}
      >
        <Alert
          severity={notice.severity}
          onClose={() => setNotice((previous) => ({ ...previous, open: false }))}
          sx={{ width: "100%" }}
        >
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}